const { assert } = require('../common/assert');
const { BaseBinGeometry } = require('./base-bin-geometry');
const { RectangularContainer } = require('./rectangular-container');
const { XYRectangle } = require('./shapes');
const { isSurface } = require('../surface');
const Transform = require('../transform');

const POCB_SENSOR_HEIGHT_OFFSET = -0.051562; /* m - this comes from eng drawings */
exports.RectangularBinGeometry = class RectangularBinGeometry extends BaseBinGeometry {
  constructor(bin) {
    super(bin, rectangularBinContainer(bin));
  }

  get isCircular() {
    return false;
  }

  sensorMidpoint() {
    return [
      this.bin.dimensions.sensorPosition.leftOffset,
      this.bin.dimensions.sensorPosition.frontOffset,
      this.bin.dimensions.totalHeight + this.bin.dimensions.sensorPosition.heightOffset + POCB_SENSOR_HEIGHT_OFFSET,
    ];
  }

  estimatedCameraAngleForBin() {
    // TODO pan and tilt tags?
    return { tilt: 0, pan: 0 };
  }

  binXYCoordinateToHeightMapCoordinate([x, y], resolution) {
    const { x1, y1, metresPerHeightMapPixel } = this.usableHeightMapCoordinates(resolution);
    return [
      Math.floor(x1 + x / metresPerHeightMapPixel),
      Math.floor(y1 + y / metresPerHeightMapPixel),
    ];
  }

  heightMapXYCoordinateToBinCoordinate([x, y], resolution) {
    const { x1, y1, metresPerHeightMapPixel } = this.usableHeightMapCoordinates(resolution);
    return [
      (x - x1) * metresPerHeightMapPixel,
      (y - y1) * metresPerHeightMapPixel,
    ];
  }

  isHeightMapCoordinateInBin(x, y, resolution) {
    const { x1, y1, x2, y2 } = this.usableHeightMapCoordinates(resolution);
    return x >= x1 && x <= x2 && y >= y1 && y <= y2;
  }

  metresPerHeightMapPixel(resolution) {
    const maxDimension = Math.max(this.bin.dimensions.body.width, this.bin.dimensions.body.depth);
    return maxDimension / resolution;
  }

  usableHeightMapCoordinates(resolution) {
    const metresPerHeightMapPixel = this.metresPerHeightMapPixel(resolution);
    const widthPixels = Math.ceil(this.bin.dimensions.body.width / metresPerHeightMapPixel) - 1;
    const depthPixels = Math.ceil(this.bin.dimensions.body.depth / metresPerHeightMapPixel) - 1;
    const x1 = Math.floor((resolution - widthPixels) / 2);
    const y1 = Math.floor((resolution - depthPixels) / 2);
    const x2 = x1 + widthPixels;
    const y2 = y1 + depthPixels;

    return { x1, y1, x2, y2, metresPerHeightMapPixel };
  }

  changeSurfaceToBinsCoordinateSystem(surface, tilt = 0, pan = 0) {
    if (!isSurface(surface)) {
      throw new Error('surface must be a Surface');
    }

    // Rotate the surface (using pan and tilt parameters) around the device's origin of rotation
    const cameraTransform = Transform.translate(0, 0, POCB_SENSOR_HEIGHT_OFFSET)
      .then(Transform.rotate(tilt, pan, 0))
      .then(Transform.translate(0, 0, -POCB_SENSOR_HEIGHT_OFFSET))
      .then(Transform.translate(...this.sensorMidpoint()));

    return surface.transform(cameraTransform);
  }
};

function rectangularBinContainer(bin) {
  assert(bin.dimensions.shape === 'rectangular', 'Bin must be rectangular');
  const { hopper, body } = bin.dimensions;
  const rectangles = [
    // top to bottom
    new XYRectangle({ x1: 0, y1: 0, x2: body.width, y2: body.depth, z: hopper.height + body.height }),
    new XYRectangle({ x1: 0, y1: 0, x2: body.width, y2: body.depth, z: hopper.height }),
    new XYRectangle({
      x1: hopper.leftOffset,
      y1: hopper.frontOffset,
      x2: hopper.leftOffset + hopper.width,
      y2: hopper.frontOffset + hopper.depth,
      z: 0,
    }),
  ];
  return new RectangularContainer(rectangles);
}
